import { jsPDF } from 'jspdf';

import { fetchAsBase64 } from './pdf-arabic.util';
import { IExportBrandingOptions } from '../provider/export-branding';

/**
 * A logo resolved to something jsPDF can place, with its natural proportions.
 */
export interface IExportLogo {
  /** Base64 payload, without a data-URI prefix. */
  base64: string;
  /** Image format passed to `addImage`. */
  format: string;
  /** Natural width in pixels, used to preserve the aspect ratio. */
  width: number;
  /** Natural height in pixels. */
  height: number;
}

/**
 * Layout constants for the generated page frame, in points.
 */
export const PDF_CHROME = {
  /** Page margin on every side. */
  MARGIN: 40,
  /** Height the logo is drawn at. */
  LOGO_HEIGHT: 26,
  /** Baseline of the header title. */
  HEADER_BASELINE: 44,
  /** Distance from the bottom edge to the footer baseline. */
  FOOTER_OFFSET: 24,
  /** First usable y position for body content. */
  CONTENT_TOP: 72,
  /** Font size of the header title. */
  TITLE_FONT_SIZE: 13,
  /** Font size of the footer. */
  FOOTER_FONT_SIZE: 8,
} as const;

/**
 * Cache of resolved logos, keyed by URL. A logo is fetched once per session.
 */
const logoCache = new Map<string, Promise<IExportLogo>>();

/**
 * Resolves the branding logo into a placeable image.
 *
 * @param branding - Branding options carrying the logo URL.
 * @returns The resolved logo, or `null` when it cannot be loaded.
 *
 * @remarks
 * Never throws. A missing logo is a cosmetic problem — failing the whole
 * document over it would be worse than a header that falls back to text.
 */
export async function resolveExportLogo(branding: IExportBrandingOptions): Promise<IExportLogo | null> {

  const { logoUrl } = branding;

  if (!logoUrl) {
    return null;
  }

  let pending = logoCache.get(logoUrl);

  if (pending == null) {
    pending = loadLogo(logoUrl);
    logoCache.set(logoUrl, pending);
  }

  try {
    return await pending;
  }
  catch (error: unknown) {
    logoCache.delete(logoUrl);
    console.warn(`[export] logo "${logoUrl}" could not be loaded; the header falls back to text.`, error);

    return null;
  }
}

/**
 * Draws the header of the current page: logo at the start edge, title centred.
 *
 * @param doc - The document being written.
 * @param options - Title, logo and direction.
 */
export function drawPdfHeader(
  doc: jsPDF,
  options: { title: string; logo: IExportLogo | null; isRtl: boolean },
): void {

  const { title, logo, isRtl } = options;
  const pageWidth = doc.internal.pageSize.getWidth();

  if (logo != null) {
    const width = (logo.width / logo.height) * PDF_CHROME.LOGO_HEIGHT;
    const x = isRtl ? pageWidth - PDF_CHROME.MARGIN - width : PDF_CHROME.MARGIN;

    doc.addImage(logo.base64, logo.format, x, PDF_CHROME.MARGIN - 22, width, PDF_CHROME.LOGO_HEIGHT);
  }

  if (title.trim().length === 0) {
    return;
  }

  doc.setFontSize(PDF_CHROME.TITLE_FONT_SIZE);
  doc.text(title, pageWidth / 2, PDF_CHROME.HEADER_BASELINE, { align: 'center' });
}

/**
 * Draws the footer of the current page: caption at the start edge, page
 * counter at the end edge.
 *
 * @param doc - The document being written.
 * @param options - Caption, counter and direction.
 *
 * @remarks
 * Both strings arrive already translated and already reordered for the
 * direction. Composing the counter here would mean this file resolving copy,
 * which belongs to the strategy that owns the document's language.
 */
export function drawPdfFooter(
  doc: jsPDF,
  options: { text: string; counter: string; isRtl: boolean },
): void {

  const { text, counter, isRtl } = options;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const baseline = pageHeight - PDF_CHROME.FOOTER_OFFSET;

  const startX = isRtl ? pageWidth - PDF_CHROME.MARGIN : PDF_CHROME.MARGIN;
  const endX = isRtl ? PDF_CHROME.MARGIN : pageWidth - PDF_CHROME.MARGIN;
  const startAlign = isRtl ? 'right' : 'left';
  const endAlign = isRtl ? 'left' : 'right';

  doc.setFontSize(PDF_CHROME.FOOTER_FONT_SIZE);

  if (text.trim().length > 0) {
    doc.text(text, startX, baseline, { align: startAlign });
  }

  if (counter.trim().length > 0) {
    doc.text(counter, endX, baseline, { align: endAlign });
  }
}

/**
 * Fetches a logo and measures it.
 *
 * @param url - The logo URL.
 * @returns The resolved logo.
 */
async function loadLogo(url: string): Promise<IExportLogo> {

  const base64 = await fetchAsBase64(url);
  const format = (url.split('.').pop() ?? 'PNG').toUpperCase();
  const { width, height } = await measureImage(`data:image/${format.toLowerCase()};base64,${base64}`);

  return { base64, format, width, height };
}

/**
 * Reads an image's natural dimensions.
 *
 * @param dataUri - The image as a data URI.
 * @returns Its natural size.
 */
function measureImage(dataUri: string): Promise<{ width: number; height: number }> {

  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error('Export logo could not be decoded.'));
    image.src = dataUri;
  });
}
